import { ApiResult, apiUrl } from '../../../api';
import { GenericSearchResult } from '../../../components/Filter/types';
import {
  CreateDashboardDto,
  CreateDashboardWidgetDto,
  DashboardDto,
  DashboardWidgetDto,
} from '../../../models/Dashboard';
import { getAccessToken } from '../../../store/authEffect';
import fetchWithRetry from '../../../util/fetchWithRetry';

export const apiFetchSearch: <ResultData = unknown, CalculatedData = unknown>(
  searchType: string,
  searchString: string,
  forceThreshold: number,
  forceResult: boolean,
  signal?: AbortSignal
) => Promise<
  ApiResult<GenericSearchResult<ResultData, CalculatedData>>
> = async (searchType, searchString, forceThreshold, forceResult, signal) => {
  const result = await fetchWithRetry(
    `${apiUrl}/search/Search?searchType=${searchType}&searchString=${searchString}&forceThreshold=${forceThreshold}&forceResult=${forceResult}`,
    {
      headers: { Authorization: `Bearer ${await getAccessToken()}` },
      method: 'GET',
      signal,
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

// Get dashboard for current user
export const apiFetchDashboard = async (
  projectId?: string
): Promise<ApiResult<DashboardDto>> => {
  const url = projectId
    ? `${apiUrl}/dashboard/project/${projectId}/projectDashboard` // <-- Pfad-Parameter
    : `${apiUrl}/dashboard/Dashboard`; // optional ohne ID

  const result = await fetchWithRetry(url, {
    headers: { Authorization: `Bearer ${await getAccessToken()}` },
  });

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;

  return {
    result,
    data,
  };
};

// Create a new dashboard
export const apiCreateDashboard = async (
  dashboardDto: CreateDashboardDto
): Promise<ApiResult<DashboardDto>> => {
  const result = await fetchWithRetry(`${apiUrl}/dashboard/Dashboard`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${await getAccessToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(dashboardDto),
  });

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;

  return {
    result,
    data,
  };
};

export const apiCreateProjectDashboard = async (
  projectId: string,
  dashboardDto: CreateDashboardDto
): Promise<ApiResult<DashboardDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/dashboard/project/${projectId}/projectDashboard`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dashboardDto),
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

export const apiDeleteDashboard = async (
  dashboardId: string
): Promise<ApiResult<void>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/dashboard/Dashboard/${dashboardId}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${await getAccessToken()}` },
    }
  );

  return {
    result,
    data: null,
  };
};

// Delete a project dashboard
export const apiDeleteProjectDashboard = async (
  projectId: string,
  dashboardId: string
): Promise<ApiResult<void>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/dashboard/project/${projectId}/projectDashboard/${dashboardId}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${await getAccessToken()}` },
    }
  );

  return {
    result,
    data: null,
  };
};

// Create a new dashboard widget
export const apiCreateDashboardWidget = async (
  dashboardId: string,
  widgetDto: CreateDashboardWidgetDto
): Promise<ApiResult<DashboardWidgetDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/dashboard/${dashboardId}/dashboardwidget`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(widgetDto),
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;

  return {
    result,
    data,
  };
};

// Create a new project dashboard widget
export const apiCreateProjectDashboardWidget = async (
  projectId: string,
  dashboardId: string,
  widgetDto: CreateDashboardWidgetDto
): Promise<ApiResult<DashboardWidgetDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/dashboard/project/${projectId}/projectDashboardWidget?dashboardId=${dashboardId}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(widgetDto),
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

// Update a dashboard widget
export const apiUpdateDashboardWidget = async (
  dashboardId: string,
  widgetId: string,
  widgetDto: DashboardWidgetDto
): Promise<ApiResult<DashboardWidgetDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/dashboard/${dashboardId}/DashboardWidget/${widgetId}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(widgetDto),
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;

  return {
    result,
    data,
  };
};

export const apiUpdateProjectDashboardWidget = async (
  projectId: string,
  dashboardId: string,
  widgetId: string,
  widgetDto: DashboardWidgetDto
): Promise<ApiResult<DashboardWidgetDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/dashboard/project/${projectId}/projectDashboardWidget/${dashboardId}/widget/${widgetId}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(widgetDto),
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;

  return {
    result,
    data,
  };
};

export const apiUpdateProjectDashboard = async (
  dashboardId: string,
  projectId: string,
  dashboardDto: CreateDashboardDto
): Promise<ApiResult<DashboardDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/dashboard/project/${projectId}/projectDashboard/${dashboardId}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dashboardDto),
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

// Delete a dashboard widget
export const apiDeleteDashboardWidget = async (
  dashboardId: string,
  widgetId: string
): Promise<ApiResult<void>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/dashboard/${dashboardId}/DashboardWidget/${widgetId}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${await getAccessToken()}` },
    }
  );

  return {
    result,
    data: null,
  };
};

// Delete a dashboard widget
export const apiDeleteProjectDashboardWidget = async (
  projectId: string,
  dashboardId: string,
  widgetId: string
): Promise<ApiResult<void>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/dashboard/project/${projectId}/projectDashboardWidget/${dashboardId}/widget/${widgetId}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${await getAccessToken()}` },
    }
  );

  return {
    result,
    data: null,
  };
};
